import axios from 'axios';
import { 
  getFromStorage, 
  removeFromStorage,
  setToStorage 
} from '../utils/storage';
import { 
  API_BASE_URL, 
  TOKEN_STORAGE_KEY, 
  REFRESH_TOKEN_STORAGE_KEY 
} from '../utils/constants';

// Importar função de limpeza (evitando import circular usando dynamic import)
let cleanupExpiredTokens = null;

// ✅ SEMÁFORO GLOBAL - Variáveis de controle para refresh de tokens
let refreshPromise = null;           // Promise do refresh em andamento
let refreshInProgress = false;       // Flag de controle simples
let refreshAttempts = 0;            // Contador de tentativas
const MAX_REFRESH_ATTEMPTS = 3;     // Máximo de tentativas

// Lazy import para evitar dependência circular
const getCleanupFunction = async () => {
  if (!cleanupExpiredTokens) {
    try {
      const authModule = await import('./authService');
      cleanupExpiredTokens = authModule.cleanupExpiredTokens;
    } catch (error) {
      console.warn('Failed to import cleanupExpiredTokens:', error);
    }
  }
  return cleanupExpiredTokens;
};

// Criar instância do Axios com URL base
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(
  (config) => {
    // Limpar tokens expirados antes de cada requisição (apenas se já carregado)
    if (cleanupExpiredTokens) {
      try {
        cleanupExpiredTokens();
      } catch (error) {
        console.warn('Failed to cleanup expired tokens:', error);
      }
    }
    
    const token = getFromStorage(TOKEN_STORAGE_KEY, false);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ✅ FUNÇÃO PROTEGIDA: Renovar token com semáforo global
const refreshTokenDirect = async () => {
  // ✅ PROTEÇÃO: Se já há um refresh em andamento, aguardar o resultado
  if (refreshPromise) {
    console.log('🔄 [REFRESH] Refresh já em andamento - aguardando resultado existente');
    return refreshPromise;
  }

  // ✅ PROTEÇÃO: Verificar máximo de tentativas
  if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
    console.error('🔄 [REFRESH] Máximo de tentativas excedido - forçando logout');
    refreshAttempts = 0;
    throw new Error('Max refresh attempts exceeded');
  }

  console.log(`🔄 [REFRESH] Iniciando refresh de token (tentativa ${refreshAttempts + 1}/${MAX_REFRESH_ATTEMPTS})`);
  refreshInProgress = true;
  refreshAttempts++;

  // ✅ SEMÁFORO: Criar promise única para todas as requisições simultâneas
  refreshPromise = (async () => {
    try {
      const refreshTokenValue = getFromStorage(REFRESH_TOKEN_STORAGE_KEY, false);
      if (!refreshTokenValue) {
        throw new Error('Refresh token not available');
      }

      // Fazer requisição direta sem usar a instância api (evita loop)
      const response = await fetch(`${API_BASE_URL}/auth/token/refresh/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: refreshTokenValue
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Error refreshing token');
      }

      const data = await response.json();
      
      // Atualizar token de acesso no armazenamento
      setToStorage(TOKEN_STORAGE_KEY, data.access_token);
      
      // Se o backend retornar um novo refresh token, atualizá-lo também
      if (data.refresh_token) {
        setToStorage(REFRESH_TOKEN_STORAGE_KEY, data.refresh_token);
      }

      // ✅ SUCESSO: Resetar contador de tentativas
      refreshAttempts = 0;
      console.log('🔄 [REFRESH] Token renovado com sucesso');
      
      return data;
    } catch (error) {
      console.error('🔄 [REFRESH] Erro durante renovação:', error);
      
      // ✅ ERRO CRÍTICO: Se token blacklisted, resetar tentativas para forçar novo login
      if (error.message && error.message.includes('blacklisted')) {
        console.warn('🔄 [REFRESH] Token blacklisted detectado - resetando tentativas');
        refreshAttempts = MAX_REFRESH_ATTEMPTS; // Forçar limite
      }
      
      throw error;
    } finally {
      // ✅ LIMPEZA: Sempre limpar o semáforo
      refreshInProgress = false;
      refreshPromise = null;
      console.log('🔄 [REFRESH] Semáforo limpo');
    }
  })();

  return refreshPromise;
};

// Interceptor para tratar erros de resposta
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Assinatura vencida (fora da tolerância): o backend bloqueia tudo menos a renovação
    if (error.response?.status === 402 && error.response?.data?.code === 'subscription_expired') {
      if (window.location.pathname !== '/renew') {
        window.location.assign('/renew');
      }
      return Promise.reject(error);
    }
    
    // Verificar se é erro 401 (não autorizado) e não é uma tentativa de refresh token
    if (error.response?.status === 401 && !originalRequest._retry && 
        !originalRequest.url?.includes('login') && 
        !originalRequest.url?.includes('auth/token') &&
        !originalRequest.url?.includes('token/refresh')) {
      
      originalRequest._retry = true;
      
      try {
        // Verificar se há refresh token disponível antes de tentar
        const refreshTokenValue = getFromStorage(REFRESH_TOKEN_STORAGE_KEY, false);
        if (!refreshTokenValue) {
          console.warn('🔄 [REFRESH] No refresh token available for automatic renewal');
          throw new Error('No refresh token available');
        }
        
        // ✅ PROTEÇÃO: Usar função protegida com semáforo
        console.log('🔄 [REFRESH] Request 401 detectado - iniciando refresh automático');
        const response = await refreshTokenDirect();
        
        // Atualizar o header com o novo token
        const newToken = response.access_token;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
        console.log('🔄 [REFRESH] Request original sendo repetido com novo token');
        // Repetir a requisição original
        return api(originalRequest);
      } catch (refreshError) {
        console.error('🔄 [REFRESH] Error refreshing token:', refreshError);

        // Determina se o erro é definitivamente de autenticação (token inválido/expirado/revogado)
        // ou se é uma falha temporária (rede, servidor fora do ar).
        // Só devemos limpar os tokens no primeiro caso.
        const errMsg = (refreshError.message || '').toLowerCase();
        const isDefinitiveAuthFailure = (
          errMsg.includes('invalid') ||
          errMsg.includes('expired') ||
          errMsg.includes('blacklisted') ||
          errMsg.includes('revoked') ||
          errMsg.includes('revogado') ||
          errMsg.includes('not available') ||
          errMsg.includes('max refresh attempts') ||
          errMsg.includes('no refresh token')
        );

        if (isDefinitiveAuthFailure) {
          // Token inválido/revogado/expirado: remover tudo e redirecionar para login.
          console.warn('🧹 [CLEANUP] Falha definitiva de autenticação — removendo todos os tokens');
          removeFromStorage(TOKEN_STORAGE_KEY);
          removeFromStorage(REFRESH_TOKEN_STORAGE_KEY);
          window.dispatchEvent(new CustomEvent('auth:logout'));
        } else {
          // Erro de rede, servidor indisponível ou erro desconhecido:
          // preserva o refresh token (ainda válido) — na próxima visita à página
          // o checkAuth() fará o refresh silencioso automaticamente.
          console.warn('🧹 [CLEANUP] Erro temporário — preservando refresh token para recuperação automática');
          removeFromStorage(TOKEN_STORAGE_KEY);
        }

        return Promise.reject(refreshError);
      }
    }
    
    // Tratar outros erros
    return Promise.reject(error);
  }
);

export default api; 